import React, { useEffect, useRef, useState } from 'react';
import { Formatter, Renderer, Stave, StaveNote, StaveTie, Voice } from 'vexflow';
import type { MusicalToken, TimeSignature } from '../../types/music';
import type { SymbolAnalysisHighlight } from '../../types/payloads/symbol_analysis.types';
import { mapEventsToVexSpecs, truncateSpecsToBarCapacity, type ScoreRenderEvent, type VexNoteSpec } from './vexFlowMapper';
import { ensureSalaVexFonts, resetSalaVexMetricsForPrint } from './vexFonts';
import { applyPrintSvgInk } from './vexPrintInk';
import { fitHostSvgToStave } from './vexSvgFit';
import { createNoteFromSpec, styleNoteDots } from './vexNoteRender';
import { VEX_PRINT, VEX_SCREEN } from './vexPrintTheme';
import { drawBeamsForPattern } from './vexBeamBuilder';
import { buildLayoutForPhraseBar, flattenPhraseBarLayouts } from './phraseBarLayout';
import { layoutFlatPhraseTies, tiesFromScoreEvents } from './vexTieUtils';

export interface PhraseScoreBar {
  barNumber: number;
  scoreEvents?: ScoreRenderEvent[];
  patternTokens?: MusicalToken[];
  dottedIndices?: number[];
  intraBarTies?: [number, number][];
}

export interface VexPhraseScoreProps {
  bars: PhraseScoreBar[];
  timeSignature?: TimeSignature;
  keySignature?: string;
  printMode?: boolean;
  className?: string;
  caption?: string;
  highlight?: SymbolAnalysisHighlight;
}

function beatsFromTimeSignature(ts: TimeSignature): { numBeats: number; beatValue: number } {
  const [n, d] = ts.split('/').map(Number);
  return { numBeats: n || 4, beatValue: d || 4 };
}

function maxBarBeats(timeSignature: TimeSignature): number {
  const { numBeats, beatValue } = beatsFromTimeSignature(timeSignature);
  return numBeats * (4 / beatValue);
}

function staveWidthForPhrase(
  barCount: number,
  beatsPerBar: number,
  noteCount: number,
  printMode: boolean,
  hasKey: boolean,
): number {
  const perBeat = printMode ? 32 : 38;
  const header = (printMode ? 48 : 54) + (hasKey ? (printMode ? 48 : 54) : 0) + (printMode ? 58 : 68);
  const totalBeats = barCount * beatsPerBar;
  return Math.max(printMode ? 360 : 420, header + totalBeats * perBeat + noteCount * (printMode ? 6 : 8));
}

function drawMeasureBarline(
  ctx: ReturnType<Renderer['getContext']>,
  stave: Stave,
  x: number,
  color: string,
) {
  const yTop = stave.getYForLine(0);
  const yBottom = stave.getYForLine(4);
  ctx.setStrokeStyle(color);
  ctx.setLineWidth(VEX_PRINT.barlineWidth);
  ctx.beginPath();
  ctx.moveTo(x, yTop);
  ctx.lineTo(x, yBottom);
  ctx.stroke();
}

function noteCenterX(stave: Stave, note: StaveNote | undefined, fallbackIdx: number, total: number): number {
  if (note?.getAbsoluteX) {
    try {
      return note.getAbsoluteX() + 6;
    } catch {
      /* fallback */
    }
  }
  const noteStart = stave.getNoteStartX();
  const noteEnd = stave.getNoteEndX();
  const slotWidth = (noteEnd - noteStart) / Math.max(total, 1);
  return noteStart + fallbackIdx * slotWidth + slotWidth * 0.5;
}

function drawHighlightMarker(
  ctx: ReturnType<Renderer['getContext']>,
  stave: Stave,
  notes: StaveNote[],
  highlight: SymbolAnalysisHighlight | undefined,
  tiePairs: [number, number][],
  color: string,
  printMode: boolean,
) {
  if (!highlight?.label) return;
  const markerColor = printMode ? '#c2410c' : '#fb923c';
  ctx.setFont('Arial, sans-serif', 11, 'bold');
  ctx.setFillStyle(markerColor);

  let x = stave.getX() + 18;
  let y = stave.getYForLine(0) - 8;

  if (highlight.type === 'key_signature') {
    y = stave.getYForLine(1) - 4;
  } else if (highlight.type === 'tie') {
    const idx = highlight.tokenIndex ?? 0;
    const pair = tiePairs.find(([a, b]) => a === idx || b === idx) ?? tiePairs[0];
    if (pair) {
      x =
        (noteCenterX(stave, notes[pair[0]], pair[0], notes.length) +
          noteCenterX(stave, notes[pair[1]], pair[1], notes.length)) /
        2;
    } else {
      x = noteCenterX(stave, notes[idx], idx, notes.length);
    }
    y = stave.getYForLine(0) - 12;
  } else if (highlight.type === 'dot' && highlight.tokenIndex != null) {
    x = noteCenterX(stave, notes[highlight.tokenIndex], highlight.tokenIndex, notes.length);
    y = stave.getYForLine(0) - 10;
  }

  ctx.fillText(highlight.label, x - 4, y);
  ctx.setStrokeStyle(markerColor);
  ctx.setLineWidth(1);
  ctx.beginPath();
  ctx.arc(x + 2, y - 6, 9, 0, Math.PI * 2);
  ctx.stroke();
}

/** Un solo pentagrama continuo con varios compases y ligaduras intra/inter compás. */
export function VexPhraseScore({
  bars,
  timeSignature = '4/4',
  keySignature,
  printMode = false,
  className = '',
  caption,
  highlight,
}: VexPhraseScoreProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const color = printMode ? VEX_PRINT.noteColor : '#f4f0e8';
  const theme = printMode ? VEX_PRINT : VEX_SCREEN;
  const { numBeats, beatValue } = beatsFromTimeSignature(timeSignature);
  const beatsPerBar = maxBarBeats(timeSignature);

  useEffect(() => {
    if (failed || !bars.length) return;
    const host = hostRef.current;
    if (!host) return;

    let cancelled = false;

    (async () => {
      try {
        await ensureSalaVexFonts();
        if (printMode) resetSalaVexMetricsForPrint();
        if (cancelled) return;

        host.innerHTML = '';

        const barLayouts = bars.map((bar) => buildLayoutForPhraseBar(bar, timeSignature));
        const { specs: allSpecs, cellRanges } = flattenPhraseBarLayouts(barLayouts);
        if (!allSpecs.length) return;

        const barEvents = bars.map((bar) => bar.scoreEvents ?? []);
        const tieInFlags = barEvents.map((events) =>
          events.length ? events[0]!.tieStop === true && events[0]!.tieStart !== true : false,
        );
        const tieOutFlags = barEvents.map((events) => {
          if (!events.length) return false;
          const mapped = mapEventsToVexSpecs(events);
          return mapped.tieOutToNext;
        });
        const flatLayout = layoutFlatPhraseTies(barEvents, tieInFlags, tieOutFlags);

        const totalBeats = beatsPerBar * bars.length;
        const staveY = theme.staveY;
        const hasKey = Boolean(keySignature && keySignature !== 'C');
        const staveW = staveWidthForPhrase(bars.length, beatsPerBar, allSpecs.length, printMode, hasKey);
        const canvasH = theme.canvasHeight;
        const canvasW = staveW + 20;

        const renderer = new Renderer(host, Renderer.Backends.SVG);
        renderer.resize(canvasW, canvasH);
        const ctx = renderer.getContext();

        const staveX = printMode ? 10 : 12;
        const stave = new Stave(staveX, staveY, staveW);
        try {
          stave.addClef('treble');
        } catch {
          /* clave no disponible */
        }
        if (hasKey) {
          try {
            stave.addKeySignature(keySignature!);
          } catch {
            /* armadura no reconocida */
          }
        }
        const [top, bottom] = timeSignature.split('/');
        stave.addTimeSignature(`${top}/${bottom}`);
        stave.setContext(ctx);
        stave.draw();

        const notes = allSpecs.map((spec) => createNoteFromSpec(spec, color, printMode));
        const voice = new Voice({ numBeats: totalBeats, beatValue });
        voice.setStrict(false);
        voice.addTickables(notes);
        const formatWidth = Math.max(120, stave.getNoteEndX() - stave.getNoteStartX() - 8);
        new Formatter().joinVoices([voice]).format([voice], formatWidth);

        notes.forEach((note, noteIdx) => {
          note.setStave(stave);
          note.draw();
          if (allSpecs[noteIdx]?.dotted) styleNoteDots(note, color, ctx);
        });

        drawBeamsForPattern(ctx, notes, allSpecs, cellRanges);

        for (let b = 1; b < flatLayout.barStarts.length; b += 1) {
          const startIdx = flatLayout.barStarts[b]!;
          const note = notes[startIdx];
          let x = stave.getNoteStartX() + 4;
          if (note?.getAbsoluteX) {
            try {
              x = note.getAbsoluteX() - 10;
            } catch {
              /* fallback */
            }
          }
          drawMeasureBarline(ctx, stave, x, color);
        }

        drawMeasureBarline(ctx, stave, stave.getX() + stave.getWidth(), color);

        for (const [from, to] of flatLayout.tiePairs) {
          if (notes[from] && notes[to]) {
            try {
              const tie = new StaveTie({
                firstNote: notes[from]!,
                lastNote: notes[to]!,
                firstIndexes: [0],
                lastIndexes: [0],
              });
              tie.setContext(ctx);
              tie.setStyle({ strokeStyle: color, lineWidth: 1.6 });
              tie.draw();
            } catch {
              /* par inválido */
            }
          }
        }

        drawHighlightMarker(ctx, stave, notes, highlight, flatLayout.tiePairs, color, printMode);

        fitHostSvgToStave(host, stave, canvasW, {
          top: 30,
          bottom: 22,
          left: 8,
          right: 18,
        });

        if (printMode) applyPrintSvgInk(host, color);

        const svg = host.querySelector('svg');
        if (svg) {
          svg.classList.add('vex-phrase-score__svg');
          if (printMode) {
            svg.classList.add('vex-phrase-score__svg--print');
            svg.style.height = `${VEX_PRINT.svgHeight}px`;
            svg.style.width = 'auto';
            svg.style.maxWidth = '100%';
          }
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bars, timeSignature, keySignature, color, failed, printMode, numBeats, beatValue, beatsPerBar, highlight]);

  if (failed) {
    return (
      <div className={`vex-phrase-score-fallback ${className}`.trim()}>
        {caption && <p className="vex-phrase-score__caption">{caption}</p>}
        <p className="text-xs opacity-70">No se pudo renderizar la frase.</p>
      </div>
    );
  }

  return (
    <div
      className={`vex-phrase-score${printMode ? ' vex-phrase-score--print' : ''} ${className}`.trim()}
    >
      {caption && <p className="vex-phrase-score__caption">{caption}</p>}
      <div ref={hostRef} className="vex-phrase-score__host" />
    </div>
  );
}
